import { useMemo, useState } from 'react'
import { useGroupMenuEngineering } from '../../hooks/useGroupMenuEngineering.js'
import { useGroupMenuEngineeringRead } from '../../hooks/useGroupMenuEngineeringRead.js'
import { AGENT_LABELS } from '../../lib/agents.js'
import KitchenLoader from '../KitchenLoader.jsx'
import AgentRanBadge from '../AgentRanBadge.jsx'
import MenuEngineeringScatter from '../MenuEngineeringScatter.jsx'
import { Skeleton, SkeletonList } from '../Skeleton.jsx'

// GroupMenuEngineering — the cross-unit menu engineering overlay (Ship 5).
// Rendered from GroupDashboardScreen when view === 'menu-engineering'.
//
// TWO decoupled data paths:
//   • useGroupMenuEngineering — the scatter rows (RLS read of the group
//     overlay view). The scatter ALWAYS renders from these, regardless of
//     the AI read's phase.
//   • useGroupMenuEngineeringRead — the AI strategic read (cached brief +
//     edge fn). Loading / empty / failed here NEVER blocks the scatter.
//
// House rule (React #310): ALL hooks fire BEFORE any conditional return.
// The PromotionsScreen #310 incident (2026-05-10) was a hook below a
// loading early-return — do not repeat it. NO AI-cost / model text.

// Stable color ramp for unit dots + chips, keyed by the unit's index in
// the (name-sorted) units list. Same palette + chip pattern as the group
// catering pipeline (GroupCateringPipeline.UNIT_COLORS / UnitChip).
const UNIT_COLORS = [
  { bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' }, // blue
  { bg: '#FEF3F2', text: '#B42318', dot: '#F04438' }, // red
  { bg: '#ECFDF3', text: '#027A48', dot: '#12B76A' }, // green
  { bg: '#FFFAEB', text: '#B54708', dot: '#F79009' }, // amber
  { bg: '#F4F3FF', text: '#5925DC', dot: '#7A5AF8' }, // violet
  { bg: '#FDF2FA', text: '#C11574', dot: '#EE46BC' }, // pink
  { bg: '#F0F9FF', text: '#026AA2', dot: '#0BA5EC' }, // cyan
]

const LOADER_MESSAGES = [
  'Reading each location’s menu mix…',
  'Comparing margins across units…',
  'Finding the cross-unit play…',
  'Writing the strategic read…',
]

export default function GroupMenuEngineering({ groupId, onBack }) {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const { rows, units, hasData, loading, error, refresh } = useGroupMenuEngineering(groupId)
  const { phase, read, generatedAt, regenerate } = useGroupMenuEngineeringRead(groupId)
  const [unitFilter, setUnitFilter] = useState('all') // 'all' | <restaurant_id>

  // Stable color per unit, by index in the name-sorted units list.
  const unitColorById = useMemo(() => {
    const m = new Map()
    units.forEach((u, idx) => m.set(u.restaurant_id, UNIT_COLORS[idx % UNIT_COLORS.length]))
    return m
  }, [units])

  // restaurant_id -> display name, for the scatter tooltip.
  const unitNameById = useMemo(() => {
    const m = new Map()
    units.forEach((u) => m.set(u.restaurant_id, u.restaurant_name))
    return m
  }, [units])

  // Rows visible in the scatter: 'all' or a single filtered unit.
  const scatterRows = useMemo(() => {
    if (unitFilter === 'all') return rows
    return rows.filter((r) => r.restaurant_id === unitFilter)
  }, [rows, unitFilter])

  // Stable color resolver passed to the scatter in unit-color mode.
  const unitColorFor = (restaurantId) => unitColorById.get(restaurantId)?.dot || '#6b7280'

  const generatedLabel = useMemo(() => formatGeneratedAt(generatedAt), [generatedAt])

  // Every row has no movement (0 units sold or non-positive margin) — the
  // view returned rows but there's nothing to plot meaningfully. Show a
  // friendly "no recent sales" state instead of a bare empty plot. Memoized
  // ABOVE the early returns (React #310).
  const noRecentSales = useMemo(() => {
    if (rows.length === 0) return false
    return rows.every((r) => Number(r.units_90d || 0) === 0 || Number(r.cm_per_unit || 0) <= 0)
  }, [rows])

  // ---- Early returns (all hooks above). ----
  if (loading) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-3 h-64 w-full" />
          <SkeletonList rows={3} className="mt-4" />
        </section>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-red-900/70">
            Group menu engineering
          </div>
          <div className="mt-2 text-sm text-red-800">{error}</div>
          <button
            type="button"
            onClick={refresh}
            className="mt-2 rounded-md px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </section>
      </div>
    )
  }

  if (!hasData || noRecentSales) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
            Group menu engineering
          </div>
          {noRecentSales ? (
            <p className="mt-2 text-sm text-black/65 leading-snug">
              No recent sales at any location to map yet. Once your locations
              record sales on menu items that have a recipe, the cross-unit
              matrix will populate here.
            </p>
          ) : (
            <p className="mt-2 text-sm text-black/65 leading-snug">
              Need menu items with both sales history AND a recipe across your
              locations to map the cross-unit matrix. Add recipes in{' '}
              <span className="font-semibold">Settings → Recipes</span> per
              location and import a longer sales history to populate this view.
            </p>
          )}
        </section>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      <BackButton onBack={onBack} />

      {/* ---------- Scatter card (renders from FULL rows, never gated by
                     the AI read) ----------------------------------- */}
      <section className="mt-3 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
          Group menu engineering
        </div>
        <p className="mt-1 text-xs text-black/60 leading-snug">
          Last 90 days, all locations. Each dot is one item at one location,
          colored by location. Popularity (units sold) vs profit per item.
        </p>

        {/* Unit filter chips: All | each unit. */}
        <nav aria-label="Filter by location" className="mt-3 overflow-x-auto">
          <div className="inline-flex min-w-full items-center gap-2 pb-1">
            <UnitChip
              label="All units"
              active={unitFilter === 'all'}
              onClick={() => setUnitFilter('all')}
            />
            {units.map((u) => (
              <UnitChip
                key={u.restaurant_id}
                label={u.restaurant_name}
                color={unitColorById.get(u.restaurant_id)}
                active={unitFilter === u.restaurant_id}
                onClick={() => setUnitFilter(u.restaurant_id)}
              />
            ))}
          </div>
        </nav>

        {/* Scatter — unit-color mode, no restaurant-relative thresholds
            (they differ per unit, so a single threshold line is wrong). */}
        <div className="mt-4">
          <MenuEngineeringScatter
            rows={scatterRows}
            thresholds={null}
            colorBy="unit"
            unitColorFor={unitColorFor}
            unitNameById={unitNameById}
          />
        </div>
      </section>

      {/* ---------- AI strategic read (charcoal card, decoupled) ----- */}
      <section
        className="relative mt-4 rounded-2xl bg-charcoal-hero p-5 shadow-sm"
        aria-label={AGENT_LABELS.groupStrategistRead}
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] font-medium text-charcoal-text-secondary">
            {AGENT_LABELS.groupStrategistRead}
          </div>
          <div className="flex items-baseline gap-2">
            {phase === 'ready' && (
              <AgentRanBadge generatedAt={generatedAt} tone="dark" />
            )}
            {(phase === 'ready' || phase === 'empty' || phase === 'error') && (
              <button
                type="button"
                onClick={() => regenerate()}
                className="text-[11px] text-charcoal-text-secondary hover:text-white"
                aria-label="Refresh strategic read"
              >
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Generating — KitchenLoader, dark tone (never blue spinner). */}
        {phase === 'generating' && (
          <div className="mt-3">
            <KitchenLoader align="row" size="sm" tone="dark" messages={LOADER_MESSAGES} />
          </div>
        )}

        {/* Idle — calm prompt. */}
        {phase === 'idle' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Tap to compose your cross-unit strategic read.
            </p>
            <button
              type="button"
              onClick={() => regenerate()}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Generate
            </button>
          </div>
        )}

        {/* Ready — headline + the three cross-unit sections. */}
        {phase === 'ready' && read && (
          <>
            {read.headline && (
              <div className="mt-3 text-[16px] sm:text-[18px] font-medium leading-snug text-white">
                {read.headline}
              </div>
            )}
            <ReadSection title="Across your units" items={read.cross_unit_comparisons} />
            <ReadSection title="Porting opportunities" items={read.porting_opportunities} />
            <ReadSection title="Review for replacement" items={read.cull_candidates} />
            {generatedLabel && (
              <div className="mt-4 text-right text-[10px] text-charcoal-text-secondary">
                {generatedLabel}
              </div>
            )}
          </>
        )}

        {/* Empty — deterministic nudge. Scatter above still rendered. */}
        {phase === 'empty' && read && (
          <>
            <div className="mt-3 text-[15px] font-medium leading-snug text-white">
              {read.headline || "We couldn't analyze enough menu data across your units yet."}
            </div>
            {read.narrative && (
              <p className="mt-2 text-[12px] leading-relaxed text-charcoal-text-tertiary">
                {read.narrative}
              </p>
            )}
            {read.retryable === true && (
              <button
                type="button"
                onClick={() => regenerate()}
                className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
              >
                Refresh
              </button>
            )}
          </>
        )}

        {/* Error — calm deterministic line + retry. Never the raw error. */}
        {phase === 'error' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              We couldn't analyze enough data yet — tap to try again.
            </p>
            <button
              type="button"
              onClick={() => regenerate()}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

function ReadSection({ title, items }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : []
  if (list.length === 0) return null
  return (
    <div className="mt-4">
      <div className="text-[11px] font-medium text-charcoal-text-secondary">{title}</div>
      <ul className="mt-2 space-y-2">
        {list.map((line, i) => (
          <li
            key={i}
            className="rounded-md border border-white/10 bg-white/[0.04] px-3 py-2.5 text-[13px] leading-relaxed text-white/90"
          >
            {line}
          </li>
        ))}
      </ul>
    </div>
  )
}

function BackButton({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 text-sm font-medium text-black/60 hover:text-black/85"
    >
      ← All units
    </button>
  )
}

function UnitChip({ label, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-black/20 bg-black/5 font-semibold text-black/85'
          : 'border-black/10 bg-white text-black/60 hover:text-black/80',
      ].join(' ')}
    >
      {color && (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: color.dot }}
          aria-hidden="true"
        />
      )}
      <span className="max-w-[12rem] truncate">{label}</span>
    </button>
  )
}

// Sentence-case "Generated 9:42 am" — matches GroupDashboardScreen.
function formatGeneratedAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const s = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `Generated ${s.replace(/\bAM\b/, 'am').replace(/\bPM\b/, 'pm')}`
}
